import React, { useCallback, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from "./components/Sidebar"
import TopBar from "./components/TopBar"
import Dashboard from "./screen/Dashboard"
import Upload from "./screen/Dashboard/Upload"
import FileView from "./screen/FileView"
import RemotePicker from "./screen/RemotePicker"
import Servers from "./screen/Servers"
import Cron from "./screen/Cron"
import Footer from "./components/Footer"
import xml2js from 'xml2js';
import styled from 'styled-components';
import {Login} from './components/LoginView'
import {RegisterForm} from './components/RegisterForm'
import { AuthProvider, useAuth } from './AuthContext';

const breakpoints = {
  mobile: '768px',
  tablet: '1024px'
};

const AppShell = styled.div`
  min-height: 100vh;
  background-color: #f8f9fb;
`;

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const Body = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #f5f7fb;
`;

const Content = styled.main`
  flex: 1;
  min-height: 0;
`;

const ProtectedRoute = ({ children }) => {
  const { isLoggedIn } = useAuth();
  const location = useLocation();
  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
};

function AuthenticatedShell({
  children,
  drives,
  selectedRemote,
  onRemoteChange,
  currentPath,
  setCurrentPath,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  showSidebar = true,
}) {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  return (
    <Shell>
      <TopBar
        onMenuClick={() => setIsMobileSidebarOpen(true)}
        drives={drives}
        selectedRemote={selectedRemote}
        onRemoteChange={onRemoteChange}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        onSearchSubmit={onSearchSubmit}
      />
      <Body>
        {showSidebar && (
          <Sidebar
            isOpen={isMobileSidebarOpen}
            onClose={() => setIsMobileSidebarOpen(false)}
            selectedRemote={selectedRemote}
            currentPath={currentPath}
            setCurrentPath={setCurrentPath}
          />
        )}
        <Main>
          <Content>{children}</Content>
          <Footer />
        </Main>
      </Body>
    </Shell>
  );
}

function AppContent() {
  const [serverList, setServerList] = useState([]);
  const [selectedRemote, setSelectedRemote] = useState(null);
  const [currentPath, setCurrentPath] = useState('');
  const [searchValue, setSearchValue] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [_isLoading, setIsLoading] = useState(false);
  const { isLoggedIn } = useAuth();

  useEffect(() => {
    const fetchXml = async () => {
      if (!isLoggedIn) return;
      setIsLoading(true);
      try {
        const response = await fetch('/ServerList.xml');
        const xmlString = await response.text();
        xml2js.parseString(xmlString, (err, result) => {
          if (err) throw err;
          const parsedServers =
            result?.servers?.node?.map((svr) => ({
              name: svr.name[0],
              type: svr.type[0],
              address: svr.address[0],
              manageFolderUrl: svr.manageFolderUrl[0],
              fileList: svr.fileList[0],
              folderList: svr.folderList[0],
              uploadUrl: svr.uploadUrl[0],
              multipartsUrl: svr.multipartsUrl[0],
              downloadUrl: svr.downloadUrl[0],
              drivesUrl: svr.drivesUrl[0],
            })) || [];
          setServerList(parsedServers);
        });
      } catch (error) {
        console.error('XML 파일을 가져오거나 파싱하는 중 오류 발생:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchXml();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const handleRemoteChange = useCallback((remote) => {
    setSelectedRemote(remote);
    setCurrentPath('');
  }, []);

  const shellProps = {
    drives: serverList,
    selectedRemote,
    onRemoteChange: handleRemoteChange,
    currentPath,
    setCurrentPath,
    searchValue,
    onSearchChange: setSearchValue,
    onSearchSubmit: setSearchValue,
  };

  return (
    <AppShell>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<RegisterForm />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Navigate to="/FileView" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Dashboard"
          element={
            <ProtectedRoute>
              <AuthenticatedShell {...shellProps} showSidebar={false}>
                <Dashboard
                  selectedRemote={selectedRemote}
                  drives={serverList}
                  onRemoteChange={handleRemoteChange}
                />
              </AuthenticatedShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/Upload"
          element={
            <ProtectedRoute>
              <AuthenticatedShell {...shellProps}>
                <Upload
                  selectedRemote={selectedRemote}
                  drives={serverList}
                  onRemoteChange={handleRemoteChange}
                  currentPath={currentPath}
                  setCurrentPath={setCurrentPath}
                />
              </AuthenticatedShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/FileView"
          element={
            <ProtectedRoute>
              <AuthenticatedShell {...shellProps} showSidebar={!!selectedRemote}>
                {selectedRemote ? (
                  <FileView
                    selectedRemote={selectedRemote}
                    drives={serverList}
                    currentPath={currentPath}
                    setCurrentPath={setCurrentPath}
                    searchValue={searchValue}
                  />
                ) : (
                  <RemotePicker
                    drives={serverList}
                    selectedRemote={selectedRemote}
                    onRemoteChange={handleRemoteChange}
                  />
                )}
              </AuthenticatedShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/Servers"
          element={
            <ProtectedRoute>
              <AuthenticatedShell {...shellProps} showSidebar={false}>
                <Servers />
              </AuthenticatedShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/Cron"
          element={
            <ProtectedRoute>
              <AuthenticatedShell {...shellProps} showSidebar={false}>
                <Cron />
              </AuthenticatedShell>
            </ProtectedRoute>
          }
        />
      </Routes>
    </AppShell>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
